import React, { useEffect, useMemo, useRef, useState } from 'react'
import { eventEmitter, EventTypes } from '~editor/events'
import { Box } from './components'
import { useSelector } from '~editor/hooks/useSelector'
import { useSWRHelper } from '~editor/hooks/useSWRHelper'
import { BoxProps } from './components/box'
import {
  Accordion,
  AccordionButton as ChakraAccordionButton,
  AccordionButtonProps,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AddIcon,
  Button,
  ChevronLeftIcon,
  CloseIcon,
  MinusIcon,
  Text,
} from '~/chakra'
import { GoToForgeEnum, IShard } from './global.type'
import { useRouter } from 'next/router'
import { DeviceMobileIcon, DeviceTabletIcon } from '~editor/components/icons'
import { Icon } from '~core/components/icon'
import { useFullscreen } from '~core/hooks/useFullscreen'

const Z_INDEX = 100

const AccordionButton = (props: AccordionButtonProps) => {
  return (
    <ChakraAccordionButton
      flex="1"
      width="100%"
      height="100%"
      padding="0"
      border="none"
      fontWeight="700"
      fontSize="1em"
      {...props}
    />
  )
}

const ArrowButton = (props: any) => {
  return (
    <Box
      width="29px"
      height="100%"
      position="absolute"
      top="0"
      right="0"
      display="flex"
      alignItems="center"
      justifyContent="center"
      color="inherit"
      {...props}
    />
  )
}

const Row = (props: BoxProps) => {
  return (
    <Box
      maxWidth="100%"
      width="300px"
      height="42px"
      paddingLeft="20px"
      paddingRight="29px"
      display="flex"
      alignItems="center"
      position="relative"
      marginBottom="1px"
      {...props}
    />
  )
}

const Variant = ({
  variant,
  isActive,
  onClick,
}: {
  variant: IShard
  isActive: boolean
  onClick: () => void
}) => {
  let DeviceIcon = DeviceTabletIcon

  if (variant?.devices?.includes('tablet')) {
    DeviceIcon = DeviceTabletIcon
  } else if (variant?.devices?.includes('mobile')) {
    DeviceIcon = DeviceMobileIcon
  }

  return (
    <Box padding="10px" width="33.33%">
      <Box
        cursor="pointer"
        onClick={onClick}
        maxWidth="75px"
        width="100%"
        height="69px"
        color={isActive ? 'rgba(73, 230, 155, 1)' : 'inherit'}
        background="#232836"
        border={`1px solid ${
          isActive ? 'rgba(73, 230, 155, 1)' : 'rgba(82, 92, 125, 0.5)'
        }`}
        borderRadius="5px"
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        padding="5px"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flex="1"
        >
          <DeviceIcon fontSize="25px" />
        </Box>
        <Text lineHeight="1" fontSize="0.9em">
          {`${variant.width}x${variant.height}`}
        </Text>
      </Box>
    </Box>
  )
}

const Shard = ({
  shard,
  isVariant,
}: {
  shard: IShard
  isVariant?: boolean
}) => {
  const router = useRouter()
  const showVariants = !isVariant && !!shard?.variants?.length
  const url = useMemo(() => {
    const urlParams = new URLSearchParams({
      shardId: `${shard.id}`,
    })

    if (shard?.source_shard?.id) {
      urlParams.append('sourceShardId', `${shard.source_shard.id}`)
    }

    return `/render?${urlParams}`
  }, [shard])

  const variants = useMemo(() => {
    if (!showVariants) return null

    return [shard, ...(shard.variants || [])]
      .sort(
        (a, b) =>
          parseInt(a.width!) * parseInt(a.height!) -
          parseInt(b.width!) * parseInt(b.height!),
      )
      .map((variant: IShard) => {
        return <Shard key={variant.id} shard={variant} isVariant />
      })
  }, [])

  const isActive = useMemo(() => {
    let isActive = false

    const shardAndVariants: IShard[] = isVariant
      ? [shard]
      : [shard, ...(shard?.variants || [])]

    for (let i = 0; i < shardAndVariants.length; i++) {
      if (
        // if any is missing, we compare 0 and 1 to know they're not the same value
        parseInt(`${shardAndVariants?.[i]?.id || '0'}`) ===
        parseInt(`${router?.query?.shardId || '1'}`)
      ) {
        isActive = true
        break
      }
    }

    return isActive
  }, [shard, router?.query?.shardId, isVariant])

  const goToShard = () => {
    if (!showVariants && router?.asPath !== url) {
      router.push(url)

      eventEmitter.emit(
        EventTypes.penpalGoToForge,
        GoToForgeEnum.collectionMenu,
        false,
      )
    }
  }

  if (isVariant) {
    return <Variant variant={shard} isActive={isActive} onClick={goToShard} />
  }

  let DeviceIcon = DeviceTabletIcon

  if (shard?.devices?.includes('tablet')) {
    DeviceIcon = DeviceTabletIcon
  } else if (shard?.devices?.includes('mobile')) {
    DeviceIcon = DeviceMobileIcon
  }

  return (
    <AccordionItem border="none">
      {({ isExpanded }) => {
        const Icon = isExpanded ? MinusIcon : AddIcon

        return (
          <Box background={isActive ? '#101219' : 'none'}>
            <AccordionButton>
              <Row
                color={isActive ? 'rgba(73, 230, 155, 1)' : 'inherit'}
                paddingLeft={isVariant ? '40px' : '20px'}
                paddingRight={isVariant ? '0' : '29px'}
                fontSize={isVariant ? '0.9em' : '1em'}
                onClick={goToShard}
              >
                <Box
                  cursor="pointer"
                  lineHeight="1"
                  flex="1"
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Text textAlign="left">{shard.name}</Text>
                  {!showVariants && (
                    <Text padding="0 5px">{`${shard.width}x${shard.height}`}</Text>
                  )}
                </Box>

                {showVariants ? (
                  <ArrowButton>
                    <Icon fontSize="0.8em" width="100%" />
                  </ArrowButton>
                ) : (
                  <ArrowButton
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <DeviceIcon fontSize="15px" />
                  </ArrowButton>
                )}
              </Row>
            </AccordionButton>

            {showVariants && (
              <AccordionPanel
                padding="0 10px"
                display="flex"
                flexWrap="wrap"
                alignItems="center"
                justifyContent="center"
              >
                {variants}
              </AccordionPanel>
            )}
          </Box>
        )
      }}
    </AccordionItem>
  )
}

export const CollectionMenu = () => {
  const [showMenu, setShowMenu] = useState(false)
  const { shard } = useSelector(({ shard }) => shard)
  const collectionId = shard?.collection?.id
  const collectionFilter = `collection__id${
    collectionId ? `=${collectionId}` : '__isnull=True'
  }`
  const { data } = useSWRHelper(
    // showMenu && `/users/@me/shards?is_variant=false`,
    showMenu &&
      `/users/@me/shards?${collectionFilter}&is_variant=false&limit=100`,
  )

  const { isFullscreenEnabled, isFullscreen, toggleFullscreen } =
    useFullscreen()

  const shards = useMemo(() => {
    const sortedShards: Record<'premium' | 'community' | 'personal', IShard[]> =
      {
        premium: [],
        community: [],
        personal: [],
      }

    const allShards: IShard[] = data?.results || []

    for (let i = 0; i < allShards.length; i++) {
      const shard = allShards[i]

      if (shard.premium) {
        sortedShards.premium.push(shard)
      } else if (shard.imported) {
        sortedShards.community.push(shard)
      } else {
        sortedShards.personal.push(shard)
      }
    }

    return sortedShards
  }, [data])

  useEffect(() => {
    const openOrCloseCollectionMenu = (where: string, showMenu = true) => {
      if (where === GoToForgeEnum.collectionMenu) setShowMenu(showMenu)
    }

    eventEmitter.on(EventTypes.penpalGoToForge, openOrCloseCollectionMenu)

    return () => {
      eventEmitter.off(EventTypes.penpalGoToForge, openOrCloseCollectionMenu)
    }
  }, [])

  useEffect(() => {
    setShowMenu(false)
  }, [shard?.id])

  const [accordionIndices, setAccordionIndices] = useState([0])
  const topLevelIndices = useRef([0])
  const shardLevelIndices = useRef<number[]>([])
  const accordionRef = useRef<HTMLDivElement | undefined>()

  useEffect(() => {
    if (accordionRef.current) {
      const topLevelButtons = accordionRef.current.querySelectorAll(
        ':scope > .chakra-accordion__item > button',
      )

      const shardLevelButtons = accordionRef.current.querySelectorAll(
        ':scope > .chakra-accordion__item  .chakra-accordion__item button',
      )

      topLevelIndices.current = Array.from(topLevelButtons).map((node) =>
        parseInt(node.getAttribute('data-index')!),
      )

      shardLevelIndices.current = Array.from(shardLevelButtons).map((node) =>
        parseInt(node.getAttribute('data-index')!),
      )
    }
  }, [shards])

  return (
    <>
      <Box
        color="#FFF"
        zIndex={Z_INDEX}
        position="fixed"
        top="0"
        left="0"
        maxWidth="100vw"
        width="300px"
        height="100vh"
        background="rgba(23, 27, 36, 1)"
        transform={`translateX(${showMenu ? 0 : '-100%'})`}
        transition="transform 150ms ease"
        display="flex"
        flexDirection="column"
        fontFamily="Proxima Nova"
        fontWeight="700"
        fontSize="14px"
      >
        <Row height="50px" paddingLeft="5px" paddingRight="5px" gap="3px">
          <Box
            cursor="pointer"
            color="rgba(155, 163, 176, 1)"
            onClick={() => setShowMenu(false)}
            padding="15px"
            display="flex"
            alignItems="center"
          >
            <CloseIcon />
          </Box>
          <Text
            lineHeight="1"
            flexGrow={1}
            textOverflow="ellipsis"
            overflow="hidden"
            whiteSpace="nowrap"
          >
            {data?.results?.[0]?.collection?.name || 'Not in Collection'}
          </Text>
          {isFullscreenEnabled && (
            <Button
              variant="outline"
              onClick={toggleFullscreen}
              isActive={isFullscreen}
            >
              <Icon.component
                color={isFullscreen ? 'white' : 'rgba(155,163,176,1.0)'}
                width="1em"
                height="1em"
                name={isFullscreen ? 'fas.faCompress' : 'fas.faExpand'}
              />
            </Button>
          )}
        </Row>

        <Box flex="1" overflowX="hidden" overflowY="auto">
          <Accordion
            id="acc"
            ref={accordionRef as any}
            defaultIndex={[0]}
            index={accordionIndices}
            allowMultiple
            onChange={(selectedAccordionIndices: number[]) => {
              setAccordionIndices(() => {
                let lastTopLevelIndex
                let lastShardLevelIndex

                const newAccordionIndices = selectedAccordionIndices.filter(
                  (index) => {
                    if (topLevelIndices.current.includes(index)) {
                      lastTopLevelIndex = index
                      return false
                    }

                    if (shardLevelIndices.current.includes(index)) {
                      lastShardLevelIndex = index
                      return false
                    }

                    return true
                  },
                )

                if (typeof lastTopLevelIndex === 'number') {
                  newAccordionIndices.unshift(lastTopLevelIndex)
                }

                if (typeof lastShardLevelIndex === 'number') {
                  newAccordionIndices.unshift(lastShardLevelIndex)
                }

                return newAccordionIndices
              })
            }}
          >
            {Object.entries(shards).map(([key, sortedShards]) => {
              return (
                !!sortedShards.length && (
                  <AccordionItem border="none">
                    <AccordionButton>
                      <Row
                        textTransform="uppercase"
                        background="#232836"
                        color="#9BA3B0"
                      >
                        {`${key} Shards`}

                        <ArrowButton>
                          <AccordionIcon fontSize="1.5em" width="100%" />
                        </ArrowButton>
                      </Row>
                    </AccordionButton>

                    <AccordionPanel padding="0">
                      {sortedShards.map((shard) => {
                        return <Shard key={shard.id} shard={shard} />
                      })}
                    </AccordionPanel>
                  </AccordionItem>
                )
              )
            })}
          </Accordion>
        </Box>

        <Row
          height="50px"
          background="#232836"
          color="#9BA3B0"
          cursor="pointer"
          onClick={() => {
            eventEmitter.emit(
              EventTypes.penpalGoToForge,
              GoToForgeEnum.collection,
            )
            setShowMenu(false)
          }}
        >
          <Box width="100%" display="flex" alignItems="center">
            <ChevronLeftIcon fontSize="1.5em" marginRight="10px" />
            {'Back To Collection'}
          </Box>
        </Row>
      </Box>

      <Box
        zIndex={(showMenu ? Z_INDEX : 0) - 1}
        position="fixed"
        top="0"
        left="0"
        width="100vw"
        height="100vh"
        background="rgba(0, 0, 0, 0.75)"
        pointerEvents={showMenu ? 'initial' : 'none'}
        opacity={+!!showMenu}
        transition={`
          z-index 150ms ease,
          opacity 150ms ease
        `}
        onClick={() => setShowMenu(false)}
      />
    </>
  )
}
